import type { Organization } from 'schema-dts';

import { config } from './config';
import type {
    LegalDocumentInput,
    LegalDocumentResult,
    LegalDocumentValidationError,
    LegalDocumentBuilder as LegalDocumentBuilderInterface,
    GeneratorOptions,
    SchemaGenerationResult,
    CompanySchemaValidationError,
} from './types/company';
import { Result, ok, fail, isOk, safeString, stripHtmlTags, isValidUrl, toAbsoluteUrl } from './utils';
import { logger } from './utils/logger';

const legalDocumentCache = new Map<string, LegalDocumentResult>();

function validateLegalDocumentInput(input: unknown): Result<LegalDocumentInput, LegalDocumentValidationError[]> {
    const errors: LegalDocumentValidationError[] = [];

    if (!input || typeof input !== 'object') {
        return fail([{ field: 'input', message: 'Legal document input must be an object' }]);
    }

    const doc = input as Record<string, unknown>;

    if (!doc.title || typeof doc.title !== 'string') {
        errors.push({ field: 'title', message: 'Title is required and must be a string', value: doc.title });
    }

    if (!doc.url || typeof doc.url !== 'string') {
        errors.push({ field: 'url', message: 'URL is required and must be a string', value: doc.url });
    } else {
        const absoluteUrl = toAbsoluteUrl(doc.url);
        if (!isValidUrl(absoluteUrl)) {
            errors.push({ field: 'url', message: 'URL must be a valid URL format', value: doc.url });
        }
    }

    const dateFields = ['datePublished', 'dateModified', 'effectiveDate'];
    for (const field of dateFields) {
        const value = doc[field];
        if (value && typeof value === 'string') {
            const date = new Date(value);
            if (isNaN(date.getTime())) {
                errors.push({ field, message: 'Date must be in valid ISO format', value });
            }
        }
    }

    if (doc.language && typeof doc.language === 'string') {
        if (doc.language.length < 2 || doc.language.length > 5) {
            errors.push({
                field: 'language',
                message: 'Language must be a valid language code (e.g., "en", "en-US")',
                value: doc.language,
            });
        }
    }

    if (errors.length > 0) {
        return fail(errors);
    }

    return ok(doc as unknown as LegalDocumentInput);
}

function updateAboutProperty(schema: Partial<LegalDocumentResult>, additionalInfo: string): void {
    if (!schema.about) {
        schema.about = {
            '@type': 'Thing',
            name: 'Legal Document',
            description: additionalInfo,
        };
    } else if (typeof schema.about === 'object' && schema.about !== null && '@type' in schema.about) {
        const aboutObj = schema.about as Record<string, unknown> & { '@type': 'Thing' };
        const currentDescription = typeof aboutObj.description === 'string' ? aboutObj.description : 'Legal document';
        aboutObj.description = `${currentDescription}. ${additionalInfo}`;
    }
}

class LegalDocumentBuilder implements LegalDocumentBuilderInterface {
    private schema: Partial<LegalDocumentResult>;

    constructor(title: string, url: string) {
        const absoluteUrl = toAbsoluteUrl(url);
        this.schema = {
            '@type': 'WebPage',
            '@id': `${absoluteUrl}#webpage`,
            name: stripHtmlTags(safeString(title)),
            url: absoluteUrl,
        };
    }

    setTitle(title: string): LegalDocumentBuilder {
        this.schema.name = stripHtmlTags(safeString(title));
        return this;
    }

    setUrl(url: string): LegalDocumentBuilder {
        const absoluteUrl = toAbsoluteUrl(url);
        this.schema.url = absoluteUrl;
        this.schema['@id'] = `${absoluteUrl}#webpage`;
        return this;
    }

    setDatePublished(date: string): LegalDocumentBuilder {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
            this.schema.datePublished = parsedDate.toISOString();
        }
        return this;
    }

    setDateModified(date: string): LegalDocumentBuilder {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
            this.schema.dateModified = parsedDate.toISOString();
        }
        return this;
    }

    setEffectiveDate(date: string): LegalDocumentBuilder {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
            updateAboutProperty(this.schema, `Effective from ${parsedDate.toISOString()}`);
        }
        return this;
    }

    setJurisdiction(jurisdiction: string): LegalDocumentBuilder {
        const cleanJurisdiction = stripHtmlTags(safeString(jurisdiction));

        updateAboutProperty(this.schema, `Under ${cleanJurisdiction} jurisdiction`);
        return this;
    }

    setPublisher(publisher: string): LegalDocumentBuilder {
        if (publisher === 'Storyblocks') {
            this.schema.publisher = config.getOrganizationSchema();
        } else {
            this.schema.publisher = {
                '@type': 'Organization',
                name: stripHtmlTags(safeString(publisher)),
            } as Organization;
        }
        return this;
    }

    setDescription(description: string): LegalDocumentBuilder {
        this.schema.description = stripHtmlTags(safeString(description));
        return this;
    }

    setLanguage(language: string): LegalDocumentBuilder {
        this.schema.inLanguage = safeString(language);
        return this;
    }

    build(): LegalDocumentResult {
        if (!this.schema.publisher) {
            this.schema.publisher = config.getOrganizationSchema();
        }

        return this.schema as LegalDocumentResult;
    }
}

export function generateLegalDocumentSchema(
    input: LegalDocumentInput,
    options: GeneratorOptions = {},
): SchemaGenerationResult<LegalDocumentResult> {
    const cacheKey = `legal-${JSON.stringify(input)}-${JSON.stringify(options)}`;

    if (!options.skipCache && legalDocumentCache.has(cacheKey)) {
        const cached = legalDocumentCache.get(cacheKey)!;
        logger.debug('Retrieved legal document schema from cache', { cacheKey });
        return {
            schema: cached,
            metadata: {
                generatedAt: new Date().toISOString(),
                cacheHit: true,
                validationPassed: true,
            },
        };
    }

    const validation = validateLegalDocumentInput(input);
    if (!isOk(validation)) {
        logger.warn('Invalid legal document input', { errors: validation.error });
        return {
            schema: {} as LegalDocumentResult,
            metadata: {
                generatedAt: new Date().toISOString(),
                cacheHit: false,
                validationPassed: false,
                errors: validation.error as CompanySchemaValidationError[],
            },
        };
    }

    const validInput = validation.data;

    const builder = new LegalDocumentBuilder(validInput.title, validInput.url);

    if (validInput.datePublished) {
        builder.setDatePublished(validInput.datePublished);
    }

    if (validInput.dateModified) {
        builder.setDateModified(validInput.dateModified);
    }

    if (validInput.effectiveDate) {
        builder.setEffectiveDate(validInput.effectiveDate);
    }

    if (validInput.jurisdiction) {
        builder.setJurisdiction(validInput.jurisdiction);
    }

    if (validInput.publisher) {
        builder.setPublisher(validInput.publisher);
    }

    if (validInput.description) {
        builder.setDescription(validInput.description);
    }

    if (validInput.language) {
        builder.setLanguage(validInput.language);
    }

    const schema = builder.build();

    if (!options.skipCache) {
        legalDocumentCache.set(cacheKey, schema);
    }

    logger.info('Generated legal document schema', {
        title: schema.name,
        url: schema.url,
        hasPublisher: !!schema.publisher,
    });

    return {
        schema,
        metadata: {
            generatedAt: new Date().toISOString(),
            cacheHit: false,
            validationPassed: true,
        },
    };
}
